import React, { useState, useRef, useEffect } from 'react';
import { Editor } from 'grapesjs';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import {
  Sparkles,
  X,
  Send,
  Wand2,
  Type,
  LayoutTemplate,
  MessageSquare,
  Copy,
  Check,
  Loader2,
  RefreshCw,
  Zap,
  FileText,
  Target,
  Megaphone
} from 'lucide-react';
import toast from 'react-hot-toast';
import { pollJobUntilComplete } from '../../../services/aiJobService';
import AiCostBadge from '../../ai/AiCostBadge';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface AIAssistantProps {
  isOpen: boolean;
  onClose: () => void;
  editor: Editor | null;
  pageName: string;
  // Quando a IA devolve um DOCUMENTO HTML completo, aplicamos a página como
  // "HTML" (fullHtml) em vez de despejar no GrapesJS — assim ela publica verbatim
  // e fica editável pelo chat "Editar com IA". O builder passa esse callback.
  onApplyAsHtml?: (html: string) => void;
}

// Documento HTML completo (começa com <!doctype html> ou <html>).
const isFullHtmlDoc = (html: string): boolean => !!html && /^\s*(<!doctype\s+html|<html[\s>])/i.test(html);

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  htmlContent?: string;
  timestamp: Date;
}

interface QuickAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  prompt: string;
  category: 'section' | 'text' | 'conversion' | 'full';
}

// Define quick action data without labels (will be translated in the component)
const quickActionData = [
  {
    id: 'full-page',
    labelKey: 'pageBuilder.ai.quickActions.fullPage',
    icon: <Sparkles className="w-4 h-4" />,
    prompt: 'Generate a complete landing page with all sections: hero, features, testimonials, CTA and FAQ',
    category: 'full'
  },
  {
    id: 'hero',
    labelKey: 'pageBuilder.ai.quickActions.hero',
    icon: <LayoutTemplate className="w-4 h-4" />,
    prompt: 'Create an attractive hero section for my landing page',
    category: 'section'
  },
  {
    id: 'features',
    labelKey: 'pageBuilder.ai.quickActions.features',
    icon: <Zap className="w-4 h-4" />,
    prompt: 'Create a features/benefits section with 3-4 items',
    category: 'section'
  },
  {
    id: 'testimonials',
    labelKey: 'pageBuilder.ai.quickActions.testimonials',
    icon: <MessageSquare className="w-4 h-4" />,
    prompt: 'Create a testimonials section with 3 satisfied customers',
    category: 'section'
  },
  {
    id: 'cta',
    labelKey: 'pageBuilder.ai.quickActions.cta',
    icon: <Target className="w-4 h-4" />,
    prompt: 'Create a persuasive call-to-action section',
    category: 'conversion'
  },
  {
    id: 'headline',
    labelKey: 'pageBuilder.ai.quickActions.headlines',
    icon: <Type className="w-4 h-4" />,
    prompt: 'Suggest 5 persuasive headlines for my landing page',
    category: 'text'
  },
  {
    id: 'copy',
    labelKey: 'pageBuilder.ai.quickActions.improve',
    icon: <FileText className="w-4 h-4" />,
    prompt: 'Improve the selected text to be more persuasive',
    category: 'text'
  },
  {
    id: 'faq',
    labelKey: 'pageBuilder.ai.quickActions.faq',
    icon: <MessageSquare className="w-4 h-4" />,
    prompt: 'Create a FAQ section with 5 items',
    category: 'section'
  },
  {
    id: 'pricing',
    labelKey: 'pageBuilder.ai.quickActions.pricing',
    icon: <Megaphone className="w-4 h-4" />,
    prompt: 'Create a pricing table with 3 plans',
    category: 'conversion'
  }
] as const;

const AIAssistant: React.FC<AIAssistantProps> = ({
  isOpen,
  onClose,
  editor,
  onApplyAsHtml,
  pageName
}) => {
  const { t } = useTranslation();
  const [currentLang, setCurrentLang] = useState(i18n.language);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<'all' | 'full' | 'section' | 'text' | 'conversion'>('all');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesRef = useRef<Message[]>(messages);

  // Keep ref in sync with state to avoid stale closures
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      setCurrentLang(lng);
    };
    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  // Create translated quickActions (will update when currentLang changes)
  const quickActions: QuickAction[] = quickActionData.map(action => ({
    id: action.id,
    label: t(action.labelKey, action.id),
    icon: action.icon,
    prompt: action.prompt,
    category: action.category as 'section' | 'text' | 'conversion' | 'full'
  }));

  // Initialize welcome message
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{
        id: '1',
        role: 'assistant',
        content: t('pageBuilder.ai.welcome', 'Ola! Sou seu assistente de IA para criar landing pages. Posso ajudar a gerar secoes, textos persuasivos, headlines e muito mais.\n\nMe conte sobre seu produto/servico e o que voce quer comunicar!'),
        timestamp: new Date()
      }]);
    }
  }, [t]);

  // Auto scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input and scroll to bottom when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      }, 100);
    }
  }, [isOpen]);

  const callAIAPI = async (userPrompt: string): Promise<{ text: string; html?: string } | null> => {
    console.log('=== CHAMANDO API DE IA ===');
    console.log('Prompt:', userPrompt);
    console.log('URL:', `${API_URL}/landing-pages/ai/chat`);

    try {
      const token = localStorage.getItem('token');
      console.log('Token exists:', !!token);

      if (!token) {
        toast.error(t('auth.sessionExpired', 'Session expired. Please log in again.'));
        return null;
      }

      const currentMessages = messagesRef.current;
      const requestBody = {
        messages: [
          ...currentMessages.filter(m => m.role !== 'assistant' || !m.htmlContent).map(m => ({
            role: m.role,
            content: m.content
          })),
          { role: 'user', content: userPrompt }
        ],
        context: {
          pageName: pageName
        },
        async: true // Use background processing to avoid Cloudflare timeout
      };

      console.log('Request body:', JSON.stringify(requestBody, null, 2));

      const response = await axios.post(
        `${API_URL}/landing-pages/ai/chat`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 30000 // 30 seconds for job submission
        }
      );

      console.log('=== RESPOSTA DA API ===');
      console.log('Status:', response.status);
      console.log('Data:', response.data);

      // If async mode, poll for result
      if (response.data.async && response.data.jobId) {
        console.log('Job enqueued:', response.data.jobId);
        toast.loading(t('pageBuilder.ai.generating', 'Generating page with AI...'), { id: 'ai-generating' });

        try {
          // Gerar leva 2-3 min. Sem contador o usuário acha que travou e recarrega
          // a página no meio — aí a geração é perdida de verdade.
          const startedAt = Date.now();
          const result = await pollJobUntilComplete(
            response.data.jobId,
            () => {
              const secs = Math.round((Date.now() - startedAt) / 1000);
              toast.loading(
                t('pageBuilder.ai.generatingElapsed', 'Gerando sua página… {{s}}s (leva 2-3 min, pode aguardar)')
                  .replace('{{s}}', String(secs)),
                { id: 'ai-generating' }
              );
            },
            2000, // Poll every 2 seconds
            600000 // 10 min: gerar a página leva 2-3 min e o teto antigo cortava no meio
          );

          toast.dismiss('ai-generating');
          console.log('Job result:', result);

          // pollJobUntilComplete already extracts result.data, so result is { text, html } directly
          if (result?.html) {
            return {
              text: result.text || t('pageBuilder.ai.pageGenerated', 'Landing page gerada com sucesso!'),
              html: result.html
            };
          }
          // AI returned text but no HTML (e.g. error message from backend)
          if (result?.text) {
            return { text: result.text };
          }
          return null;
        } catch (pollError: any) {
          toast.dismiss('ai-generating');
          // A mensagem do poll já explica o caso (timeout, job expirado, falha).
          // Antes virava um "IA indisponível" genérico que escondia o motivo.
          const detail = pollError?.message || t('errors.generic', 'Erro desconhecido');
          toast.error(detail);
          return { text: detail };
        }
      }

      // Sync response (fallback)
      if (response.data.success && response.data.data) {
        console.log('HTML recebido:', response.data.data.html ? 'SIM' : 'NÃO');
        return {
          text: response.data.data.text,
          html: response.data.data.html || undefined
        };
      }
      console.warn('AI API returned unsuccessful response:', response.data);
      return null;
    } catch (error: any) {
      console.error('=== ERRO NA API ===');
      console.error('Error:', error);
      console.error('Response:', error.response?.data);

      if (error.response?.status === 429) {
        toast.error(t('pageBuilder.ai.rateLimitError', 'AI request limit reached. Wait a few minutes.'));
      } else if (error.response?.status === 401) {
        toast.error(t('auth.sessionExpired', 'Session expired. Please log in again.'));
      } else if (error.code === 'ECONNABORTED') {
        toast.error(t('pageBuilder.ai.timeoutError', 'AI is taking too long. Try again.'));
      } else {
        toast.error(t('pageBuilder.ai.apiError', 'API Error: {{message}}').replace('{{message}}', error.message));
      }

      return null;
    }
  };

  const generateAIResponse = async (userPrompt: string): Promise<{ text: string; html?: string }> => {
    console.log('=== generateAIResponse INICIADO ===');
    console.log('Prompt recebido:', userPrompt);

    const promptLower = userPrompt.toLowerCase();

    // =================================================================
    // USAR IA PARA GERAR HTML - Templates apenas como fallback
    // =================================================================

    // Check if this is a text-only request (headlines, copy improvement, etc.)
    const isTextOnlyRequest =
      promptLower.includes('headline') ||
      promptLower.includes('titulo') ||
      promptLower.includes('títulos') ||
      promptLower.includes('suggest') ||
      promptLower.includes('sugira') ||
      promptLower.includes('sugestão') ||
      promptLower.includes('sugestões') ||
      promptLower.includes('melhora') ||
      promptLower.includes('reescrev') ||
      promptLower.includes('improve') ||
      promptLower.includes('rewrite');

    // Check if this is an HTML generation request (page or section)
    const isHtmlRequest = !isTextOnlyRequest && (
      // Full page keywords
      promptLower.includes('página completa') ||
      promptLower.includes('pagina completa') ||
      promptLower.includes('landing page') ||
      promptLower.includes('landing') ||
      promptLower.includes('gere uma') ||
      promptLower.includes('crie uma') ||
      promptLower.includes('criar uma') ||
      promptLower.includes('gerar uma') ||
      promptLower.includes('todas as seções') ||
      promptLower.includes('todas as secoes') ||
      promptLower.includes('site completo') ||
      promptLower.includes('página') ||
      promptLower.includes('pagina') ||
      // Section keywords
      promptLower.includes('hero') ||
      promptLower.includes('feature') ||
      promptLower.includes('beneficio') ||
      promptLower.includes('depoimento') ||
      promptLower.includes('testimonial') ||
      promptLower.includes('cta') ||
      promptLower.includes('call-to-action') ||
      promptLower.includes('faq') ||
      promptLower.includes('pergunta') ||
      promptLower.includes('pricing') ||
      promptLower.includes('preco') ||
      promptLower.includes('plano') ||
      promptLower.includes('secao') ||
      promptLower.includes('seção') ||
      promptLower.includes('section')
    );

    console.log('isHtmlRequest:', isHtmlRequest);

    // For HTML generation requests, use the AI API
    if (isHtmlRequest) {
      try {
        const aiResponse = await callAIAPI(userPrompt);
        console.log('>>> Resposta da API:', aiResponse);

        if (aiResponse && aiResponse.html) {
          return {
            text: aiResponse.text || 'Landing page gerada com sucesso! Clique em "Substituir Tudo" para aplicar.',
            html: aiResponse.html
          };
        }

        // If AI returned text but no HTML, still show the response
        if (aiResponse && aiResponse.text) {
          return {
            text: aiResponse.text
          };
        }

        // AI failed - show error and ask user to try again
        console.warn('AI API did not return HTML');
        toast.error(t('pageBuilder.ai.aiUnavailable', 'IA temporariamente indisponível. Tente novamente.'));
        return {
          text: t('pageBuilder.ai.aiUnavailableMessage', 'A IA está temporariamente indisponível. Por favor, tente novamente em alguns segundos.')
        };

      } catch (error) {
        console.error('AI generation failed:', error);
        toast.error(t('pageBuilder.ai.aiGenerationError', 'Erro ao gerar. Tente novamente.'));
        return {
          text: t('pageBuilder.ai.aiGenerationErrorMessage', 'Erro ao gerar a landing page com IA. Por favor, tente novamente.')
        };
      }
    }

    // =================================================================
    // For TEXT-ONLY requests, also use AI API
    // =================================================================

    // Try AI API for text requests (headlines, copy improvement, etc.)
    try {
      const aiResponse = await callAIAPI(userPrompt);

      if (aiResponse && aiResponse.text) {
        return {
          text: aiResponse.text,
          html: aiResponse.html
        };
      }
    } catch (error) {
      console.error('AI text generation failed:', error);
    }

    // Fallback responses if AI fails for text requests
    if (promptLower.includes('headline') || promptLower.includes('titulo')) {
      return {
        text: `Aqui estão 5 headlines persuasivas para sua landing page:\n\n1. **"Transforme [problema] em [benefício] em apenas [tempo]"**\n   Use números específicos para credibilidade\n\n2. **"A solução que [número] empresas escolheram para [benefício]"**\n   Prova social integrada ao título\n\n3. **"Pare de [dor do cliente]. Comece a [resultado desejado]"**\n   Contraste emocional forte\n\n4. **"O segredo que [experts/concorrentes] não querem que você saiba"**\n   Desperta curiosidade\n\n5. **"[Benefício principal] sem [principal objeção]"**\n   Elimina a maior barreira\n\nQuer que eu crie uma hero section com uma dessas headlines?`
      };
    }

    if (promptLower.includes('melhora') || promptLower.includes('reescrev')) {
      return {
        text: `Aqui estão algumas dicas para melhorar seu texto:\n\n**1. Use verbos de ação**\nTroque "Nossa solução ajuda" por "Transforme seu negócio"\n\n**2. Adicione números específicos**\nTroque "muitos clientes" por "mais de 10.000 clientes"\n\n**3. Foque nos benefícios**\nTroque "temos IA integrada" por "economize 10h/semana com automação inteligente"\n\n**4. Crie urgência**\nAdicione "Vagas limitadas" ou "Oferta válida até [data]"\n\n**5. Prova social**\nAdicione "Usado por empresas como X, Y, Z"\n\nSelecione o texto no editor e me envie para eu reescrever!`
      };
    }

    // Default response
    return {
      text: `Entendi! Para a landing page "${pageName}", posso ajudar a criar:\n\n• **Página completa**: Descreva seu negócio e eu gero uma página personalizada com IA\n• **Seções individuais**: Hero, Features, Testimonials, Pricing, FAQ, CTA\n• **Textos persuasivos**: Headlines, descrições, botões\n\n💡 **Exemplo**: "Crie uma landing page para uma academia de crossfit com hero impactante, benefícios e depoimentos"\n\nMe conte sobre seu produto/serviço!`
    };
  };
  const handleSend = async (directPrompt?: string) => {
    const promptText = directPrompt || input.trim();

    if (!promptText || isLoading) {
      return;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: promptText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await generateAIResponse(userMessage.content);

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.text,
        htmlContent: response.html,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      toast.error(t('pageBuilder.ai.responseError', 'Error generating response. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickAction = (action: QuickAction) => {
    handleSend(action.prompt);
  };

  const handleInsertHtml = (html: string, replaceAll: boolean = true) => {
    // Documento completo + "Substituir Tudo": aplica como PÁGINA HTML (fullHtml),
    // preservando head/scripts/Tailwind e habilitando o chat "Editar com IA".
    // O GrapesJS destruiria o <head> ao fazer setComponents de um doc completo.
    if (replaceAll && onApplyAsHtml && isFullHtmlDoc(html)) {
      onApplyAsHtml(html);
      toast.success(t('pageBuilder.ai.pageReplaced', 'Página aplicada com sucesso!'));
      onClose();
      return;
    }

    if (!editor) {
      toast.error(t('pageBuilder.ai.editorNotReady', 'Editor not ready'));
      return;
    }

    if (replaceAll) {
      // Limpa o canvas e insere o novo conteúdo
      editor.DomComponents.clear();
      editor.setComponents(html);
      toast.success(t('pageBuilder.ai.pageReplaced', 'Page replaced successfully!'));
    } else {
      // Adiciona ao final do canvas
      editor.DomComponents.addComponent(html);
      toast.success(t('pageBuilder.messages.sectionAdded', 'Section added to canvas!'));
    }
    onClose();
  };

  const handleCopyHtml = async (html: string, messageId: string) => {
    try {
      await navigator.clipboard.writeText(html);
      setCopiedId(messageId);
      toast.success(t('pageBuilder.ai.htmlCopied', 'HTML copiado!'));
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error(t('pageBuilder.ai.copyFailed', 'Falha ao copiar. Tente selecionar manualmente.'));
    }
  };

  const filteredActions = activeCategory === 'all'
    ? quickActions
    : quickActions.filter(a => a.category === activeCategory);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Sidebar Panel */}
      <div className="absolute right-0 top-0 bottom-0 w-full sm:w-[480px] bg-gray-900 shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">{t('pageBuilder.ai.title', 'Assistente IA')}</h2>
              <p className="text-sm text-gray-400">{t('pageBuilder.ai.subtitle', 'Gere secoes e textos com IA')}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Quick Actions */}
        <div className="px-4 py-4 border-b border-gray-700">
          {/* Category Tabs */}
          <div className="flex gap-2 mb-3 overflow-x-auto">
            {[
              { id: 'all', label: t('pageBuilder.ai.categories.all', 'Todos') },
              { id: 'full', label: `✨ ${t('pageBuilder.ai.categories.full', 'Completa')}` },
              { id: 'section', label: t('pageBuilder.ai.categories.sections', 'Seções') },
              { id: 'text', label: t('pageBuilder.ai.categories.text', 'Textos') },
              { id: 'conversion', label: t('pageBuilder.ai.categories.conversion', 'Conversão') }
            ].map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id as any)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  activeCategory === cat.id
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:text-white'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2">
            {filteredActions.map(action => (
              <button
                key={action.id}
                onClick={() => handleQuickAction(action)}
                disabled={isLoading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                {action.icon}
                {action.label}
              </button>
            ))}
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {messages.map(message => (
            <div key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[90%] rounded-2xl px-4 py-3 ${
                  message.role === 'user'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-800 text-gray-200'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>

                {/* HTML Preview & Actions */}
                {message.htmlContent && (
                  <div className="mt-3 pt-3 border-t border-gray-700">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-400">{t('pageBuilder.ai.htmlGenerated', 'HTML Gerado')}</span>
                      <button
                        onClick={() => handleCopyHtml(message.htmlContent!, message.id)}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-gray-700 hover:bg-gray-600 text-xs text-gray-300 rounded transition-colors"
                      >
                        {copiedId === message.id ? (
                          <>
                            <Check className="w-3 h-3" />
                            {t('pageBuilder.ai.copied', 'Copiado')}
                          </>
                        ) : (
                          <>
                            <Copy className="w-3 h-3" />
                            {t('pageBuilder.ai.copyHtml', 'Copiar')}
                          </>
                        )}
                      </button>
                    </div>
                    <pre className="text-xs text-gray-500 bg-gray-900 rounded-lg p-2 overflow-x-auto max-h-32">
                      {message.htmlContent.substring(0, 200)}...
                    </pre>
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => handleInsertHtml(message.htmlContent!, true)}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-xs text-white rounded-lg transition-colors font-medium"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                        {t('pageBuilder.ai.replaceAll', 'Substituir Tudo')}
                      </button>
                      <button
                        onClick={() => handleInsertHtml(message.htmlContent!, false)}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-xs text-white rounded-lg transition-colors font-medium"
                      >
                        <Wand2 className="w-3.5 h-3.5" />
                        {t('pageBuilder.ai.addToEnd', 'Adicionar ao Final')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-800 rounded-2xl px-4 py-3">
                <div className="flex items-center gap-2 text-gray-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">{t('pageBuilder.ai.generating', 'Gerando...')}</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="px-4 py-4 border-t border-gray-700">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder={t('pageBuilder.ai.placeholder', 'Descreva o que voce precisa...')}
                rows={2}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={() => handleSend()}
              disabled={!input.trim() || isLoading}
              className="px-4 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl transition-colors"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
            <AiCostBadge tone="dark" feature="ai-generation" action="landing-page-chat" unitLabel={t('aiCredits.perMessage', 'por mensagem')} />
            <p className="text-[11px] text-gray-500">
              {t('pageBuilder.ai.inputHint', 'Shift + Enter para nova linha · Enter para enviar')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAssistant;
